import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ShieldAlert, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { usernameHasProfanity, isReservedUsername } from '@/lib/profanity';

export default function ForceRenameDialog({ onDone, currentUsername }) {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const formatOk = /^[A-Za-z0-9_-]{6,24}$/.test(username);
  const sameAsOld = !!currentUsername && username.trim().toLowerCase() === currentUsername.toLowerCase();
  const valid = formatOk && !usernameHasProfanity(username) && !isReservedUsername(username) && !sameAsOld;

  const submit = async () => {
    if (sameAsOld) { toast.error(t('rename.sameAsOld')); return; }
    if (!valid) { toast.error(t('welcome.usernameError')); return; }
    setSubmitting(true);
    try {
      const res = await api.patch('/me', { username: username.trim() });
      toast.success(t('rename.done'));
      onDone?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('welcome.setupError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[2000] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="force-rename-dialog">
      <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="glass-panel rounded-3xl p-6 max-w-md w-full">
        <div className="flex items-center gap-2 mb-2 text-brand">
          <ShieldAlert className="w-5 h-5" />
          <h2 className="font-display font-bold text-xl text-brand-ink">{t('rename.title')}</h2>
        </div>
        <p className="text-sm text-brand-slate mb-4">{t('rename.body')}</p>
        <Input
          data-testid="force-rename-input"
          value={username}
          onChange={e => setUsername(e.target.value)}
          placeholder={t('welcome.usernamePlaceholder')}
          className="rounded-xl bg-black/5 border-transparent focus:bg-white focus:border-brand"
        />
        <p className="text-xs text-brand-dim mt-1.5">{t('welcome.usernameHint')}</p>
        <p className="text-xs font-medium text-[#C86F53] mt-1.5">{t('welcome.noRealName')}</p>
        {sameAsOld && (
          <p data-testid="rename-same-as-old" className="text-xs font-medium text-[#C86F53] mt-1.5">{t('rename.sameAsOld')}</p>
        )}
        <Button
          data-testid="force-rename-submit"
          onClick={submit}
          disabled={submitting || !valid}
          className="w-full mt-4 rounded-full bg-brand hover:bg-brand-hover text-white"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          {t('rename.submit')}
        </Button>
      </motion.div>
    </div>
  );
}
